import { API_BASE } from "./config";

// ── In-memory access token store ──────────────────────────────────────────────
// Kept in JS closure so it's inaccessible to injected scripts.
let _accessToken = null;

// ── Global unauthenticated callback ──────────────────────────────────────────
// Fired when a request returns 401 even after a refresh attempt (token expired mid-session).
let _onUnauthenticated = null;
export function setOnUnauthenticated(cb) { _onUnauthenticated = cb; }

export function setAccessToken(token) {
  _accessToken = token;
  // Cache in sessionStorage so reloads within the same tab skip an unnecessary refresh call
  if (token) sessionStorage.setItem("rq_access", token);
  else sessionStorage.removeItem("rq_access");
}
export function getAccessToken() {
  return _accessToken;
}
export function clearAccessToken() {
  _accessToken = null;
  sessionStorage.removeItem("rq_access");
}

// ── Token refresh ─────────────────────────────────────────────────────────────
let _refreshPromise = null;

async function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = fetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    credentials: "include", // sends the httpOnly rq_refresh cookie
  })
    .then(async (r) => {
      if (!r.ok) throw new Error("refresh_failed");
      const data = await r.json();
      if (data.accessToken) setAccessToken(data.accessToken);
      return data.accessToken;
    })
    .finally(() => {
      _refreshPromise = null;
    });
  return _refreshPromise;
}

// ── Try to restore session on app load ────────────────────────────────────────
// Calls the refresh endpoint; if the cookie is present and valid a new access
// token is issued and stored. Returns user info from the token or null.
export async function tryRestoreSession() {
  // Use cached token if still valid — avoids hitting the refresh endpoint and
  // rotating the refresh cookie on every page reload (which can cause race
  // conditions in multi-tab scenarios where the new Set-Cookie isn't saved).
  const cached = sessionStorage.getItem("rq_access");
  if (cached) {
    try {
      const payload = JSON.parse(atob(cached.split(".")[1]));
      if (payload.exp * 1000 > Date.now() + 30_000) {
        setAccessToken(cached);
        return { userId: Number(payload.sub), isAdmin: !!payload.isAdmin, isTeacher: !!payload.isTeacher };
      }
    } catch {}
    sessionStorage.removeItem("rq_access");
  }

  try {
    const token = await refreshAccessToken();
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return { userId: Number(payload.sub), isAdmin: !!payload.isAdmin, isTeacher: !!payload.isTeacher };
  } catch {
    return null;
  }
}

// ── apiFetch ──────────────────────────────────────────────────────────────────
// Drop-in replacement for fetch() that:
//   - Prepends API_BASE to relative URLs
//   - Attaches Authorization: Bearer header
//   - On 401, refreshes the access token once and retries
//   - Passes credentials: 'include' so the httpOnly cookie travels with requests
export default async function apiFetch(url, options = {}) {
  const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;

  const buildHeaders = () => ({
    ...options.headers,
    ...(_accessToken ? { Authorization: `Bearer ${_accessToken}` } : {}),
  });


  const doFetch = (headers) =>
    fetch(fullUrl, { ...options, headers, credentials: "include" });

  let res = await doFetch(buildHeaders());

  if (res.status === 401) {
    try {
      await refreshAccessToken();
    } catch {
      // Refresh failed — session is unrecoverable
      if (_onUnauthenticated) _onUnauthenticated();
      return res;
    }
    res = await doFetch(buildHeaders());
    if (res.status === 401 && _onUnauthenticated) _onUnauthenticated();
  }

  return res;
}
